/**
 * How a pin is coloured, and what the legend says.
 *
 * ONE definition for both, because a legend that disagrees with the pins is worse than no
 * legend — the reader trusts it.
 *
 * The ORDER is also the precedence used by markerStatusOf() below, for a building holding
 * several statuses. Both pages group their own rows client-side (the map plots the page they
 * are showing, so there is no server-side aggregation to do it), which is why this lives
 * here and nowhere else.
 */
export const MARKER_STATUS = [
  { value: "MIS",       label: "MIS",       color: "#8e24aa" },
  { value: "RETIRED",   label: "Retired",   color: "#607d8b" },
  { value: "REVIEWED",  label: "Reviewed",  color: "#2e7d32" },
  { value: "IN_REVIEW", label: "In Review", color: "#ef6c00" },
  { value: "VERIFIED",  label: "Verified",  color: "#1976d2" },
  { value: "TO_REVIEW", label: "New",       color: "#90a4ae" },
];

/**
 * Sale vs rental, for maps whose rows do not carry a pipeline status.
 *
 * The listings page is one: /listings/search returns every listing, sales and rentals, and
 * has no internal_status in its payload — so colouring by status there would paint every pin
 * the same grey and print a legend reading "Other". Side is the distinction that page is
 * actually about. The pipeline board DOES carry status, and that is where status-as-colour
 * belongs.
 */
export const MARKER_SIDE = [
  { value: "S",     label: "Sale",   color: "#1976d2" },
  { value: "R",     label: "Rental", color: "#00897b" },
  { value: "MIXED", label: "Both",   color: "#6a1b9a" },
];

const BY_VALUE = Object.fromEntries(MARKER_STATUS.map((s) => [s.value, s]));
const BY_SIDE = Object.fromEntries(MARKER_SIDE.map((s) => [s.value, s]));

/** A building holding both a sale and a rental is neither; it is "Both". */
function sideKey(m) {
  if (!m.sides || m.sides.length === 0) return null;
  return m.sides.length > 1 ? "MIXED" : m.sides[0];
}

/**
 * Which status a building holding several is drawn as: furthest along the pipeline wins, so
 * a building with one MIS unit and eleven untouched ones reads as MIS rather than vanishing
 * into the New bucket.
 *
 * The rule lives only here: markers are derived from the rows a page already has, so there
 * is no server-side aggregation that would need to agree with it.
 */
export function markerStatusOf(statuses) {
  const known = MARKER_STATUS.filter((s) => statuses.includes(s.value));
  if (known.length) return known[0].value;          // MARKER_STATUS is in precedence order
  return statuses.find(Boolean) ?? null;
}

/** What colours a pin, given which dimension this map is painted by. */
export function markerKey(m, colorBy) {
  return colorBy === "side" ? sideKey(m) : m.status;
}

/** A status the server sent but this file does not know still gets a pin, in grey. */
export const UNKNOWN_STATUS = { value: null, label: "Other", color: "#bdbdbd" };

export function markerStyle(m, colorBy = "status") {
  const key = markerKey(m, colorBy);
  return (colorBy === "side" ? BY_SIDE[key] : BY_VALUE[key]) || UNKNOWN_STATUS;
}

/** Only the values actually present, so the legend describes THIS map and not the enum. */
export function legendFor(markers, colorBy = "status") {
  const table = colorBy === "side" ? MARKER_SIDE : MARKER_STATUS;
  const lookup = colorBy === "side" ? BY_SIDE : BY_VALUE;
  const present = new Set(markers.map((m) => markerKey(m, colorBy)));
  const known = table.filter((s) => present.has(s.value));
  const hasUnknown = [...present].some((k) => !lookup[k]);
  return hasUnknown ? [...known, UNKNOWN_STATUS] : known;
}
